/**
 * TmsPipelinePage — Sales Pipeline / CRM for Container Trucking
 *
 * Kanban view: Prospect → Quoted → Negotiating → Won → Lost
 * Each card shows customer, estimated monthly volume, value, next action.
 *
 * AI Integration: Boox Customer Success + Jess help with:
 *   - "Who needs follow-up today?"
 *   - "Generate a quote for ABC Freight"
 *   - "What's our conversion rate this month?"
 *   - "Send follow-up to all quoted-but-no-response in 7 days"
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Users, TrendingUp, Plus, ArrowRight,
  Phone, Mail, Calendar, DollarSign,
  CheckCircle2, XCircle, Clock, BarChart3,
  MessageSquare, RefreshCcw, Filter,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/stores/app-store';

// ─── Types ─────────────────────────────────────────

type PipelineStage = 'prospect' | 'quoted' | 'negotiating' | 'won' | 'lost';

interface PipelineDeal {
  id: string;
  customerName: string;
  contactPerson: string;
  estimatedVolume: string;
  estimatedValue: number;
  services: string[];
  stage: PipelineStage;
  nextAction: string;
  nextActionDate: string;
  probability: number;
  source: string;
  daysInStage: number;
}

// ─── Demo Data ─────────────────────────────────────

const DEMO_DEALS: PipelineDeal[] = [
  // Prospects
  { id: 'D001', customerName: 'Nordic Shipping Asia', contactPerson: 'Erik Hansen', estimatedVolume: '30 TEU/mo', estimatedValue: 15000, services: ['IMP+RET', 'EXP'], stage: 'prospect', nextAction: 'Initial call', nextActionDate: '2026-03-15', probability: 10, source: 'Referral', daysInStage: 3 },
  { id: 'D002', customerName: 'Fresh Foods SG', contactPerson: 'Tan Mei Lin', estimatedVolume: '15 TEU/mo', estimatedValue: 9000, services: ['IMP'], stage: 'prospect', nextAction: 'Send intro email', nextActionDate: '2026-03-14', probability: 5, source: 'Website', daysInStage: 1 },
  { id: 'D003', customerName: 'TechParts Co', contactPerson: 'Jason Lim', estimatedVolume: '8 TEU/mo', estimatedValue: 4800, services: ['IMP+RET'], stage: 'prospect', nextAction: 'WhatsApp follow-up', nextActionDate: '2026-03-16', probability: 15, source: 'Cold outreach', daysInStage: 7 },

  // Quoted
  { id: 'D004', customerName: 'Global Chemicals Pte', contactPerson: 'Ravi Patel', estimatedVolume: '20 TEU/mo (DG)', estimatedValue: 16000, services: ['IMP+RET', 'DG'], stage: 'quoted', nextAction: 'Follow up on quote', nextActionDate: '2026-03-14', probability: 30, source: 'Trade show', daysInStage: 5 },
  { id: 'D005', customerName: 'Apex Electronics', contactPerson: 'David Wong', estimatedVolume: '25 TEU/mo', estimatedValue: 12500, services: ['IMP+RET', 'COL+EXP'], stage: 'quoted', nextAction: 'Rate negotiation call', nextActionDate: '2026-03-17', probability: 40, source: 'Referral', daysInStage: 10 },

  // Negotiating
  { id: 'D006', customerName: 'Sunrise Trading', contactPerson: 'Lee Ah Hua', estimatedVolume: '40 TEU/mo', estimatedValue: 22000, services: ['IMP+RET', 'EXP', 'T/T'], stage: 'negotiating', nextAction: 'Send revised rates', nextActionDate: '2026-03-15', probability: 60, source: 'Existing (upgrade)', daysInStage: 12 },
  { id: 'D007', customerName: 'Marine Supplies Int\'l', contactPerson: 'Ahmad Razak', estimatedVolume: '12 TEU/mo', estimatedValue: 7200, services: ['EXP'], stage: 'negotiating', nextAction: 'Contract review', nextActionDate: '2026-03-18', probability: 70, source: 'Referral', daysInStage: 8 },

  // Won
  { id: 'D008', customerName: 'ABC Freight Pte Ltd', contactPerson: 'Jennifer Tan', estimatedVolume: '35 TEU/mo', estimatedValue: 18500, services: ['IMP+RET', 'COL+EXP'], stage: 'won', nextAction: 'Onboard & first DO', nextActionDate: '2026-03-20', probability: 100, source: 'Trade show', daysInStage: 2 },

  // Lost
  { id: 'D009', customerName: 'QuickShip Logistics', contactPerson: 'Michael Ng', estimatedVolume: '10 TEU/mo', estimatedValue: 5500, services: ['IMP'], stage: 'lost', nextAction: 'Archive', nextActionDate: '', probability: 0, source: 'Website', daysInStage: 20 },
];

const STAGES: { id: PipelineStage; label: string; color: string; bgColor: string }[] = [
  { id: 'prospect', label: 'Prospect', color: 'text-gray-700', bgColor: 'bg-gray-50' },
  { id: 'quoted', label: 'Quoted', color: 'text-blue-700', bgColor: 'bg-blue-50' },
  { id: 'negotiating', label: 'Negotiating', color: 'text-amber-700', bgColor: 'bg-amber-50' },
  { id: 'won', label: 'Won', color: 'text-green-700', bgColor: 'bg-green-50' },
  { id: 'lost', label: 'Lost', color: 'text-red-700', bgColor: 'bg-red-50' },
];

// ─── Component ─────────────────────────────────────

export default function TmsPipelinePage() {
  const [deals] = useState(DEMO_DEALS);
  const { setChatPanelOpen } = useAppStore();

  const askAi = (prompt: string) => {
    setChatPanelOpen(true);
    window.dispatchEvent(new CustomEvent('boox:chat-prompt', { detail: { prompt } }));
  };

  // Pipeline stats
  const weighted = deals
    .filter(d => d.stage !== 'lost')
    .reduce((s, d) => s + d.estimatedValue * (d.probability / 100), 0);
  const totalValue = deals
    .filter(d => d.stage !== 'lost')
    .reduce((s, d) => s + d.estimatedValue, 0);
  const wonValue = deals.filter(d => d.stage === 'won').reduce((s, d) => s + d.estimatedValue, 0);
  const lostValue = deals.filter(d => d.stage === 'lost').reduce((s, d) => s + d.estimatedValue, 0);
  const winRate = deals.filter(d => d.stage === 'won' || d.stage === 'lost').length > 0
    ? Math.round((deals.filter(d => d.stage === 'won').length / deals.filter(d => d.stage === 'won' || d.stage === 'lost').length) * 100)
    : 0;

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Sales Pipeline</h1>
          <p className="text-sm text-muted-foreground">Track prospects, quotes, and conversions</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => askAi('Who needs follow-up today? List all deals with overdue next actions.')}>
            <MessageSquare className="h-4 w-4 mr-1" />
            Ask Boox
          </Button>
          <Button size="sm">
            <Plus className="h-4 w-4 mr-1" />
            Add Deal
          </Button>
        </div>
      </div>

      {/* ── Stats Bar ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Weighted Pipeline</p>
                <p className="text-lg font-bold">${Math.round(weighted).toLocaleString()}<span className="text-xs text-muted-foreground font-normal">/mo</span></p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Won (this month)</p>
                <p className="text-lg font-bold text-green-600">${wonValue.toLocaleString()}<span className="text-xs text-muted-foreground font-normal">/mo</span></p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-amber-100 flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Win Rate</p>
                <p className="text-lg font-bold">{winRate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center">
                <Users className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Active Deals</p>
                <p className="text-lg font-bold">{deals.filter(d => d.stage !== 'won' && d.stage !== 'lost').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Kanban Board ── */}
      <div className="flex gap-3 overflow-x-auto pb-4">
        {STAGES.map((stage) => {
          const stageDeals = deals.filter(d => d.stage === stage.id);
          const stageValue = stageDeals.reduce((s, d) => s + d.estimatedValue, 0);
          return (
            <div key={stage.id} className="flex flex-col min-w-[260px] max-w-[300px] flex-1">
              {/* Column header */}
              <div className={cn('rounded-t-lg px-3 py-2 flex items-center justify-between', stage.bgColor)}>
                <div className="flex items-center gap-1.5">
                  <span className={cn('text-sm font-semibold', stage.color)}>{stage.label}</span>
                  <Badge variant="secondary" className="text-[10px] px-1.5">{stageDeals.length}</Badge>
                </div>
                <span className={cn('text-xs font-medium', stage.color)}>${stageValue.toLocaleString()}</span>
              </div>

              {/* Cards */}
              <div className="flex flex-col gap-2 bg-muted/30 rounded-b-lg p-2 min-h-[200px]">
                {stageDeals.map((deal) => (
                  <Card key={deal.id} className="cursor-pointer hover:shadow-md transition-shadow">
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-start justify-between">
                        <p className="text-sm font-medium leading-tight">{deal.customerName}</p>
                        <Badge variant="outline" className="text-[10px] shrink-0 ml-1">
                          {deal.probability}%
                        </Badge>
                      </div>

                      <p className="text-xs text-muted-foreground">{deal.contactPerson}</p>

                      <div className="flex flex-wrap gap-1">
                        {deal.services.map(s => (
                          <Badge key={s} variant="secondary" className="text-[10px] px-1">{s}</Badge>
                        ))}
                      </div>

                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{deal.estimatedVolume}</span>
                        <span className="font-semibold">${deal.estimatedValue.toLocaleString()}/mo</span>
                      </div>

                      {deal.nextAction && deal.stage !== 'lost' && (
                        <div className="flex items-center gap-1 text-[10px] text-blue-600 bg-blue-50 rounded px-1.5 py-0.5">
                          <Clock className="h-3 w-3" />
                          <span className="truncate">{deal.nextAction}</span>
                          {deal.nextActionDate && <span className="text-muted-foreground ml-auto shrink-0">{deal.nextActionDate.slice(5)}</span>}
                        </div>
                      )}

                      {deal.daysInStage > 10 && deal.stage !== 'won' && deal.stage !== 'lost' && (
                        <div className="text-[10px] text-amber-600">
                          ⚠️ {deal.daysInStage} days in stage
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}

                {stageDeals.length === 0 && (
                  <div className="flex items-center justify-center h-20 text-xs text-muted-foreground">
                    No deals
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* AI Hint */}
      <div className="rounded-lg border border-dashed border-purple-300 bg-purple-50/50 p-3 flex items-start gap-2">
        <MessageSquare className="h-4 w-4 text-purple-600 mt-0.5 shrink-0" />
        <div className="text-xs text-purple-800">
          <p className="font-medium">💡 Talk to Boox in the side chat:</p>
          <p className="mt-1 text-purple-600">"Generate a rate quote for Nordic Shipping — 30 TEU/mo IMP+RET zone 3"</p>
          <p className="text-purple-600">"Send follow-up email to all quoted deals over 7 days"</p>
          <p className="text-purple-600">"What's our pipeline conversion by source channel?"</p>
        </div>
      </div>
    </div>
  );
}
